/**
 * What a laptop's last scan did not read, and why (release 2.1 item 10).
 *
 * Images need the OCR add-on, some documents over the size limit are
 * skipped, and folders the agent cannot open are passed over. Until 2.1 only
 * the agent's log on the laptop said so, and a reviewer could not tell a clean
 * laptop from one whose PowerPoints were never opened. Agent 0.0.14 (release
 * 3.1) reads PowerPoint, Word 97, e-mail, RTF and OpenDocument files itself;
 * older agents still report them as needing the add-on, so the wording covers
 * both.
 *
 * Release 3.3, the OCR add-on: the server scanner reads images and scanned
 * PDFs by OCR when the add-on is on, and says how many ("read by OCR"); with it
 * off they are "not read - OCR add-on off". Laptops never run OCR.
 */

import { FileWarning } from 'lucide-react';

import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import type { EdgeLastScanFiles } from '@/lib/api';

const INDIAN = new Intl.NumberFormat('en-IN');

export interface ReasonText {
  title: string;
  advice: string;
}

/** Plain words for each reason the agent can report. Unknown reasons still show. */
export function reasonText(reason: string): ReasonText {
  switch (reason) {
    case 'needs_extraction':
      return {
        title: 'Not read - OCR add-on off',
        advice:
          'Images and scanned PDFs need the OCR add-on. It runs only on the server scanner (in the bundle, FOCTTA_OCR=on); laptops never run OCR, so scan those folders from the server scanner. Agents older than 0.0.14 also list PowerPoint, Word 97, email, RTF and OpenDocument files here; update the agent and it reads those itself.',
      };
    case 'too_large':
      return {
        title: 'Documents over the size limit',
        advice:
          'Raise the per-file limit (SCAN_MAX_BYTES_MB) or add the text-extraction add-on. From agent 0.0.14 most larger files are read in part; PDF and older Excel files still need the higher limit.',
      };
    case 'encrypted':
      return {
        title: 'Encrypted or password-protected',
        advice:
          'Found and not opened: the file is encrypted, so any personal data in it is not in the results. Get it unlocked and rescan, or accept that it is not covered.',
      };
    case 'could_not_open':
      return {
        title: 'Could not be opened',
        advice:
          'Corrupt, or the text could not be extracted. Agents older than release 8 also list encrypted and password-protected files here; update the agent to see them apart.',
      };
    case 'no_text':
      return {
        title: 'Opened, but no readable text inside',
        advice:
          'A document with no text even after it was opened: a blank or damaged file, or a scan the OCR add-on found no text in. Its contents were not checked.',
      };
    case 'read_error':
      return {
        title: 'Could not be read',
        advice: 'Access denied, locked by another program, or removed during the scan.',
      };
    case 'not_reached':
      return {
        title: 'Not reached',
        advice: "The scan's per-run limit ran out before these files. Raise the limit or scan more often.",
      };
    case 'folder_unreadable':
      return {
        title: 'Folders that could not be opened',
        advice: 'Their contents are unknown. Usually a permission the agent does not have.',
      };
    default:
      return { title: reason.replace(/_/g, ' '), advice: '' };
  }
}

/** "Read 1,240 files. Not read: 37. Partly read (over the size limit, first part only): 2." */
export function coverageSentence(f: EdgeLastScanFiles): string {
  const parts = [`Read ${INDIAN.format(f.filesRead)} file${f.filesRead === 1 ? '' : 's'}.`];
  if ((f.filesReadByOcr ?? 0) > 0) {
    parts.push(`Read by OCR: ${INDIAN.format(f.filesReadByOcr ?? 0)}.`);
  }
  parts.push(f.filesNotRead === 0 ? 'Nothing it meant to read was skipped.' : `Not read: ${INDIAN.format(f.filesNotRead)}.`);
  if (f.filesPartlyRead > 0) {
    parts.push(`Partly read (over the size limit, first part only): ${INDIAN.format(f.filesPartlyRead)}.`);
  }
  return parts.join(' ');
}

/** Why a laptop scan read every file, in words (release 8, incremental_fs.rs). */
export function fullReasonText(reason: string | undefined): string {
  switch (reason) {
    case 'no_state':
      return 'the first scan by this agent version on this laptop';
    case 'state_damaged':
    case 'state_unprotected':
      return 'its record of earlier scans could not be trusted';
    case 'agent_changed':
      return 'the agent was updated';
    case 'classifier_changed':
      return 'the detection settings, the OCR add-on or the console this agent reports to changed';
    case 'requested':
      return 'a full scan was asked for, or the agent was enrolled again';
    case 'scheduled':
      return 'the scheduled full scan';
    case 'every_scan':
      return 'it is set to read every file on every scan';
    default:
      return reason ? reason.replace(/_/g, ' ') : 'a full scan';
  }
}

/**
 * Release 8: whether the last scan read every file or only the changed ones.
 * Null for older agents and server scanners, which report neither.
 */
export function incrementalSentence(f: EdgeLastScanFiles): string | null {
  if (f.scanMode === 'incremental') {
    const n = f.filesUnchanged ?? 0;
    return n > 0
      ? `Only changed files were read: ${INDIAN.format(n)} file${n === 1 ? ' was' : 's were'} unchanged since the last read and not opened again (the counts above include what that read found).`
      : 'Only changed files were read; every file had changed or was new.';
  }
  if (f.scanMode === 'full') return `Every file was read (${fullReasonText(f.fullScanReason)}).`;
  return null;
}

/** "pptx 12, jpg 4" */
export function typesLine(types: Array<{ ext: string; count: number }>): string {
  return types.map((t) => `${t.ext} ${INDIAN.format(t.count)}`).join(', ');
}

export function ScanCoverageCard({
  files,
  finishedAt,
}: {
  files: EdgeLastScanFiles | null;
  finishedAt: string | null;
}) {
  return (
    <GlassCard className="p-3">
      <CardHeader className="p-0 mb-2">
        <CardTitle className="text-sm inline-flex items-center gap-1.5">
          <FileWarning className="h-4 w-4" /> What the last scan did not read
        </CardTitle>
        <CardDescription className="text-[11px]">
          {finishedAt ? <>Last scan finished {new Date(finishedAt).toLocaleString()}. </> : null}
          Counts and file names only; file contents never leave the laptop.
        </CardDescription>
      </CardHeader>
      {files === null ? (
        <p className="text-xs text-muted-foreground">
          Not reported. Agents from release 2.1 send this after each scan; older agents keep it only in
          their log on the laptop.
        </p>
      ) : (
        <div className="space-y-2 text-xs">
          <p className="text-foreground/90">{coverageSentence(files)}</p>
          {incrementalSentence(files) ? (
            <p className="text-muted-foreground" data-testid="incremental-sentence">
              {incrementalSentence(files)}
            </p>
          ) : null}
          {/* A group at 0 only says the agent separates encrypted files (release 8); it is not a gap. */}
          {files.notRead.filter((g) => g.count > 0).map((g) => {
            const t = reasonText(g.reason);
            return (
              <div key={g.reason} className="rounded border border-border/60 p-2 space-y-1">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <span className="font-medium">{t.title}</span>
                  <span className="tabular-nums">{INDIAN.format(g.count)}</span>
                </div>
                {g.types.length > 0 && <div className="text-muted-foreground">{typesLine(g.types)}</div>}
                {t.advice && <div className="text-muted-foreground">{t.advice}</div>}
                {g.examples && g.examples.length > 0 && (
                  <ul className="font-mono text-[11px] text-foreground/80 space-y-0.5">
                    {g.examples.map((e) => (
                      <li key={e} className="break-all">{e}</li>
                    ))}
                    {g.count > g.examples.length && (
                      <li className="text-muted-foreground font-sans">
                        and {INDIAN.format(g.count - g.examples.length)} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            );
          })}
          {files.otherTypesTotal > 0 && (
            <p className="text-muted-foreground">
              Not scanned by design ({INDIAN.format(files.otherTypesTotal)} files: archives, programs and other
              types the agent does not read): {typesLine(files.otherTypes)}.
            </p>
          )}
        </div>
      )}
    </GlassCard>
  );
}
